import React from 'react';
import PropTypes from 'prop-types';
import styles from './Pagination.module.css';
import * as FaIcons from 'react-icons/fa';
import Select from '../../common/Select';
import { translate } from '../../../utils/Common';

const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPage = true,
  itemsPerPageOptions = [10, 25, 50, 100],
  className = '',
}) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const containerClasses = [styles.container, className].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <div className={styles.info}>
        <span className={styles.infoText}>
          {translate('global.showing')} {startItem}-{endItem} {translate('global.of')} {totalItems} {translate('global.results')}
        </span>
      </div>

      <div className={styles.controls}>
        <button
          className={styles.button}
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          aria-label={translate('pagination.firstPage')}
        >
          <FaIcons.FaAngleDoubleLeft />
        </button>

        <button
          className={styles.button}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label={translate('pagination.previousPage')}
        >
          <FaIcons.FaAngleLeft />
        </button>

        <div className={styles.pages}>
          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className={styles.ellipsis}>
                ...
              </span>
            ) : (
              <button
                key={page}
                className={`${styles.pageButton} ${page === currentPage ? styles.active : ''}`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </button>
            )
          ))}
        </div>

        <button
          className={styles.button}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label={translate('pagination.nextPage')}
        >
          <FaIcons.FaAngleRight />
        </button>

        <button
          className={styles.button}
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          aria-label={translate('pagination.lastPage')}
        >
          <FaIcons.FaAngleDoubleRight />
        </button>
      </div>

      {showItemsPerPage && onItemsPerPageChange && (
        <div className={styles.itemsPerPage}>
          <Select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            options={itemsPerPageOptions.map(option => ({
              value: option,
              label: `${option} ${translate('pagination.perPage')}`
            }))}
            size="sm"
          />
        </div>
      )}
    </div>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  itemsPerPage: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onItemsPerPageChange: PropTypes.func,
  showItemsPerPage: PropTypes.bool,
  itemsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  className: PropTypes.string,
};

export default Pagination;
